import axios from 'axios';
import qs from 'qs';
import Vue from 'vue';
import posthog from 'posthog-js';
import { camelCasePlatformPayload } from 'config/platforms';
import { deepHashSnakeCase } from './convert_keys';

const httpClient = axios.create({
  transformRequest: [
    (data => (data ? deepHashSnakeCase(data) : undefined)),
    ...axios.defaults.transformRequest,
  ],
  transformResponse: [...axios.defaults.transformResponse, camelCasePlatformPayload],
  paramsSerializer: params => qs.stringify(deepHashSnakeCase(params), { arrayFormat: 'brackets', skipNulls: true }),
});

const csrf = document.querySelector('meta[name="csrf-token"]');
if (csrf) {
  httpClient.defaults.headers.common = {
    'X-CSRF-TOKEN': csrf.getAttribute('content'),
  };
}

httpClient.interceptors.request.use((config) => {
  const sessionId = posthog.get_session_id?.();
  if (sessionId) {
    config.headers['X-PostHog-Session-ID'] = sessionId;
  }
  return config;
});

httpClient.interceptors.response.use(response => response, (error) => {
  if (!error.response || !error.response.status) {
    window.EventBus.$emit('server-error', {});
    return Promise.reject(error);
  }

  if (error.response.status === 422 && error.config.csrfRefetch !== true) {
    return Vue.auth.fetch().then((csrfToken) => {
      error.config.headers['X-CSRF-TOKEN'] = csrfToken;
      httpClient.defaults.headers.common = { 'X-CSRF-TOKEN': csrfToken };
      document.querySelector('meta[name="csrf-token"]')?.setAttribute('content', csrfToken);
      error.config.csrfRefetch = true;
      return httpClient.request(error.config);
    });
  } if (error.response.status === 429) {
    window.EventBus.$emit('too-many-requests-error', {});
  } else if (error.response.status === 403) {
    window.EventBus.$emit('forbidden-error', {});
  } else if (error.response.status === 404 && error.response.data.error === 'NotFoundError') {
    if (!error.config.skipNotFoundRedirect) {
      window.EventBus.$emit('not-found-error', {});
    }
  } else if (error.response.status === 401) {
    if (!error.config.skipUnauthorizedRedirect) {
      window.EventBus.$emit('unauthorized-error', {});
    }
  } else if (error.response.status === 503) {
    window.EventBus.$emit('timeout-error', {});
  } else if (error.response.status >= 500) {
    window.EventBus.$emit('server-error', {});
  }

  return Promise.reject(error.response.data);
});


httpClient.streamPost = (url, data, { onEvent } = {}) => {
  const abortCtrl = new AbortController();
  const csrfToken = httpClient.defaults.headers.common['X-CSRF-TOKEN'];

  const sessionId = posthog.get_session_id?.();

  const promise = (async () => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        ...(sessionId ? { 'X-PostHog-Session-ID': sessionId } : {}),
      },
      credentials: 'same-origin',
      body: JSON.stringify(data),
      signal: abortCtrl.signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let done = false;

    while (!done) {
      const result = await reader.read(); // eslint-disable-line no-await-in-loop
      done = result.done;

      if (done) break;

      buffer += decoder.decode(result.value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop();

      lines.filter(line => line.trim()).forEach((line) => {
        const eventMatch = line.match(/^event: (.+)$/m);
        const dataMatch = line.match(/^data: (.+)$/m);

        if (!eventMatch || !dataMatch) return;

        onEvent(eventMatch[1], JSON.parse(dataMatch[1]));
      });
    }
  })();

  return { promise, abort: () => abortCtrl.abort() };
};

export default httpClient;
